'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Menu } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/lib/i18n'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const ITEMS = [
  { href: '/play', labelKey: 'play.nav.home', match: (p: string) => p === '/play' },
  { href: '/play/game', labelKey: 'play.nav.play', match: (p: string) => p.startsWith('/play/game') },
  { href: '/play/me', labelKey: 'play.nav.howAmIDoing', match: (p: string) => p.startsWith('/play/me') },
  { href: '/play/library', labelKey: 'play.nav.library', match: (p: string) => p.startsWith('/play/library') },
  { href: '/play/inbox', labelKey: 'play.nav.inbox', match: (p: string) => p.startsWith('/play/inbox') },
  { href: '/play/leaderboard', labelKey: 'play.nav.leaderboard', match: (p: string) => p.startsWith('/play/leaderboard') },
]

/** Desktop nav — the inline link row (md and up). */
export function PlayerNav() {
  const { t } = useTranslation()
  const pathname = usePathname() || ''
  return (
    <nav className="hidden md:flex items-center gap-1 text-sm">
      {ITEMS.map((item) => {
        const active = item.match(pathname)
        return (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              'px-3 py-1.5 rounded-md transition-colors',
              active
                ? 'bg-muted text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            )}
          >
            {t(item.labelKey)}
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * Mobile nav — a hamburger → dropdown of the SAME items (below md). Without this
 * the entire play nav vanished on phones (PlayerNav is `hidden md:flex`), leaving
 * a learner with no way to move between Play / How am I doing / Library / etc.
 */
export function PlayerMobileMenu() {
  const { t } = useTranslation()
  const pathname = usePathname() || ''
  return (
    <div className="md:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t('play.nav.openMenu')}>
            <Menu className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {ITEMS.map((item) => {
            const active = item.match(pathname)
            return (
              <DropdownMenuItem key={item.href} asChild>
                <Link href={item.href} className={cn('w-full cursor-pointer', active && 'font-medium text-foreground')}>
                  {t(item.labelKey)}
                </Link>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
